import { useCallback, useRef } from 'react';

interface KnobProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  /** Renders the numeric read-out. */
  format?: (v: number) => string;
  /** Skews the response so the useful range isn't squashed at one end. */
  curve?: number;
  bipolar?: boolean;
  color?: string;
  size?: number;
  onChange: (v: number) => void;
  onCommit?: () => void;
}

/** Vertical-drag knob. Shift = fine, double-click = reset to centre/min. */
export function Knob({
  label,
  value,
  min,
  max,
  step = 0.001,
  format,
  curve = 1,
  bipolar = false,
  color = '#8b5cf6',
  size = 38,
  onChange,
  onCommit,
}: KnobProps) {
  const drag = useRef<{ y: number; v: number } | null>(null);

  const norm = (v: number) => Math.pow((v - min) / (max - min), 1 / curve);
  const denorm = (n: number) => min + Math.pow(Math.max(0, Math.min(1, n)), curve) * (max - min);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      (e.target as Element).setPointerCapture(e.pointerId);
      drag.current = { y: e.clientY, v: norm(value) };
    },
    [value, min, max, curve],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!drag.current) return;
      const speed = e.shiftKey ? 0.0015 : 0.006;
      const dn = drag.current.v + (drag.current.y - e.clientY) * speed;
      const raw = denorm(dn);
      const snapped = Math.round(raw / step) * step;
      onChange(Math.max(min, Math.min(max, snapped)));
    },
    [onChange, min, max, step, curve],
  );

  const end = () => {
    if (drag.current) onCommit?.();
    drag.current = null;
  };

  const n = norm(value);
  const sweep = 270;
  const angle = -135 + n * sweep;
  const r = size / 2 - 4;
  const cx = size / 2;
  const cy = size / 2;
  const arc = (from: number, to: number) => {
    const a0 = ((from - 90) * Math.PI) / 180;
    const a1 = ((to - 90) * Math.PI) / 180;
    const large = Math.abs(to - from) > 180 ? 1 : 0;
    const sweepFlag = to > from ? 1 : 0;
    return `M ${cx + r * Math.cos(a0)} ${cy + r * Math.sin(a0)} A ${r} ${r} 0 ${large} ${sweepFlag} ${
      cx + r * Math.cos(a1)
    } ${cy + r * Math.sin(a1)}`;
  };
  const zero = bipolar ? -135 + norm((min + max) / 2) * sweep : -135;

  return (
    <div className="flex flex-col items-center gap-1 select-none">
      <svg
        width={size}
        height={size}
        className="cursor-ns-resize touch-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={end}
        onPointerCancel={end}
        onDoubleClick={() => {
          onChange(bipolar ? (min + max) / 2 : min);
          onCommit?.();
        }}
      >
        <path d={arc(-135, 135)} stroke="#2a323b" strokeWidth={3} fill="none" strokeLinecap="round" />
        {Math.abs(angle - zero) > 0.5 && (
          <path
            d={arc(Math.min(zero, angle), Math.max(zero, angle))}
            stroke={color}
            strokeWidth={3}
            fill="none"
            strokeLinecap="round"
          />
        )}
        <line
          x1={cx}
          y1={cy}
          x2={cx + (r - 4) * Math.cos(((angle - 90) * Math.PI) / 180)}
          y2={cy + (r - 4) * Math.sin(((angle - 90) * Math.PI) / 180)}
          stroke="#e6ebef"
          strokeWidth={2}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={2.5} fill="#5a6773" />
      </svg>
      <div className="label leading-none">{label}</div>
      <div className="mono text-[10px] text-ink-300 leading-none">
        {format ? format(value) : value.toFixed(2)}
      </div>
    </div>
  );
}
